import React, { useCallback, useState } from 'react';
import {
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Switch,
    Typography
} from '@mui/material';
import { CustomizedDataGrid } from '../customized_data_grid';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import DeleteConfirmationDialog from './DeleteModal';
import { activateKPIConfig, deactivateKPIConfig, deleteKPIConfig } from 'service/service';
import DialogueModal from 'components/Modal/DialogModal';
import { KPIForm } from './kpiForm';
import { exportToXlsx } from 'components/exportUtils';
import { checkRole } from 'utils/roleUtils';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { useTheme } from "@mui/material/styles";
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { ReactComponent as RedirectSelectedIcon } from '../../assets/PepWise2.0/RedirectSelected.svg';
import { ReactComponent as DeleteMenuIcon } from '../../assets/PepWise2.0/Delete-Menu-Icon.svg';

const IdFormatter = ({ row, navigate }: { row: any; navigate: any }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                cursor: 'pointer',
            }}
            onClick={() => navigate(`/kpiFramework/configs/${row?._id}`)}
        >
            <Typography
                variant='tableRowFont'
                title={row?._id}
                sx={{
                    color: '#009BD8',
                    textDecoration: 'underline',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                    mr: 1,
                }}
            >
                {row?._id}
            </Typography>
            <RedirectSelectedIcon
                width="20px"
                height="20px"
                style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                }}
            />
        </Box>
    );
};

const StatusFormatter = ({ row, colors }: { row: any; colors: any }) => {
    return (
        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Chip
                variant='chipLabel'
                style={{
                    width: '100%',
                    height: '100%',
                    color: row.isActive ? "#01C008" : "#D82B51",
                    backgroundColor: row.isActive ? "#01C00826" : "#D82B5133",
                    margin: 0,
                    maxWidth: '200px'
                }}
                sx={{
                    width: '100%',
                    color: `${row.isActive ? "#01C008" : "#D82B51"} !important`,
                    backgroundColor: `${row.isActive ? "#01C00826" : "#D82B5133"} !important`,
                }}
                label={row.isActive ? "ACTIVE" : "INACTIVE"}
            />
        </div>
    );
};

export const KPIConfigList = ({ kpiConfigs, totalCount, updateConfigs, page, setPage, pageSize, setPageSize, sortingInfo, setSortingInfo, handleSorting }: any) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [filterData] = useState<any[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [controller] = usePepwiseAuthController();
    const defaultColumns = ['ID', 'KPI Name', 'Program', 'Project', 'Sector', 'Status', 'Frequency', 'Source', 'Last Run', 'Next Run',
        'Created By', 'Updated By', 'Created At', 'Updated At', 'Action'];

    const { role } = controller;

    const handleClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleSortingFun = useCallback((sortingInfo: any) => {
        handleSorting({ sortBy: sortingInfo.sortBy, sortDir: sortingInfo.sortDir });
    }, [updateConfigs]);


    const handleToggleStatus = async () => {
        if (selectedRow) {
            if (selectedRow.isActive) {
                await deactivateKPIConfig(selectedRow._id)
            } else {
                await activateKPIConfig(selectedRow._id)
            }
            updateConfigs({ page, pageSize })
        }
        handleClose();
    };

    const handleDeleteConfirm = async () => {
        if (selectedRow) {
            await deleteKPIConfig(selectedRow._id)
            updateConfigs({ page, pageSize })
        }
        setOpenDialog(false);
    };

    const handlePageChange = (data: any, page: any) => {
        setPage(page)
        updateConfigs({ page, pageSize })
    }

    const handlePageSizeChange = (event: any) => {
        setPageSize(parseInt(event.target.value))
        updateConfigs({ page, pageSize: event.target.value })
    }

    const navigate = useNavigate();
    const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

    function getColumns() {
        const columns: any[] = [
            {
                key: "_id", name: "ID", minWidth: 50, width: 300, sortable: false,
                formatter({ row }: any) {
                    return <IdFormatter row={row} navigate={navigate} />;
                }
            },
            { key: "name", name: "KPI Name", width: 200, minWidth: 150, sortable: false },
            { key: "program", name: "Program", minWidth: 95, sortable: true, },
            { key: "project", name: "Project", minWidth: 95, sortable: true, },
            { key: "sector", name: "Sector", minWidth: 95, sortable: true, },
            {
                key: "isActive",
                name: "Status",
                minWidth: 50,
                sortable: false,
                cellClass: 'status-chip-cell',
                formatter({ row }: any) {
                    return <StatusFormatter row={row} colors={colors} />;
                }
            },
            { key: "update_frequency", name: "Frequency", minWidth: 95 },
            { key: "source_type", name: "Source", minWidth: 95 },
            {
                key: "last_run_time", name: "Last Run", minWidth: 95,
                formatter({ row }: any) {
                    return (row.last_run_time ? moment(row.last_run_time).format('yyyy-MM-DD HH:mm') : '')
                }
            },
            {
                key: "next_run_time", name: "Next Run", minWidth: 95,
                formatter({ row }: any) {
                    return (row.next_run_time ? moment(row.next_run_time).format('yyyy-MM-DD HH:mm') : '')
                }
            },
            { key: "createdByUser", name: "Created By", minWidth: 95, sortable: false },
            { key: "updatedByUser", name: "Updated By", minWidth: 95, sortable: false },
            { key: "createdDt", name: "Created At", minWidth: 95 },
            {
                key: "updatedAt", name: "Updated At", minWidth: 95,
                formatter({ row }: any) {
                    return (row.updatedAt ? moment(row.updatedAt).format('yyyy-MM-DD HH:mm') : '')
                }
            },
        ];

        if (isAllowed) {
            columns.push({
                key: "action",
                name: "Action",
                minWidth: 95,
                sortable: false,
                formatter({ row }: any) {
                    const handleDeleteClick = () => {
                        setOpenDialog(true);
                        handleClose();
                    };
                    return (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                                aria-label="edit"
                                size="small"
                                onClick={() => {
                                    setSelectedRow(row)
                                    setShowCreateModal(true)
                                }}
                                sx={{
                                    '& .MuiSvgIcon-root': {
                                        color: "#39ACE7 !important",
                                    },
                                }}
                            >
                                <EditIcon fontSize="small" sx={{ color: "#39ACE7 !important" }} />
                            </IconButton>

                            <IconButton
                                aria-label="more"
                                size="small"
                                onClick={(event) => handleClick(event, row)}
                                sx={{
                                    '& .MuiSvgIcon-root': {
                                        color: "#39ACE7 !important",
                                    },
                                }}
                            >
                                <MoreVertIcon fontSize="small" sx={{ color: "#39ACE7 !important" }} />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                PaperProps={{
                                    elevation: 0,
                                    sx: {
                                        backgroundColor: '#FFFFFF !important',
                                        borderRadius: '6px',
                                        border: '1px solid #E5E7EB',
                                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                                        width: '200px',
                                        minWidth: '200px',
                                        overflow: 'hidden',
                                        p: 0,
                                        '& .MuiList-root': {
                                            padding: '0 !important',
                                        },
                                        '& .MuiMenuItem-root': {
                                            height: 'auto !important',
                                            minHeight: 'unset !important',
                                            padding: '12px 24px !important',
                                            boxSizing: 'border-box !important',
                                        },
                                    },
                                }}
                            >
                                <MenuItem
                                    onClick={handleToggleStatus}
                                    sx={{
                                        padding: '12px 24px !important',
                                        height: 'auto !important',
                                        minHeight: 'unset !important',
                                        boxSizing: 'border-box !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'transparent',
                                        borderBottom: '1px solid #E5E7EB',
                                        '&:hover': {
                                            backgroundColor: '#D4ECFD !important',
                                        },
                                    }}
                                >
                                    <Switch
                                        checked={!selectedRow?.isActive}
                                        size="small"
                                        sx={{
                                            width: 34,
                                            height: 20,
                                            padding: 0,
                                            mr: 1.5,
                                            pointerEvents: 'none',
                                            '& .MuiSwitch-switchBase': {
                                                padding: '2px',
                                                '&.Mui-checked': {
                                                    transform: 'translateX(14px)',
                                                    color: '#FFFFFF',
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: '#39ACE7',
                                                        opacity: 1,
                                                        border: 'none',
                                                    },
                                                },
                                                '&:not(.Mui-checked)': {
                                                    color: '#FFFFFF',
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: '#71758F',
                                                        opacity: 1,
                                                        border: 'none',
                                                    },
                                                },
                                            },
                                            '& .MuiSwitch-thumb': {
                                                boxSizing: 'border-box',
                                                width: 16,
                                                height: 16,
                                                backgroundColor: '#FFFFFF',
                                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.25)',
                                            },
                                            '& .MuiSwitch-track': {
                                                borderRadius: 999,
                                                backgroundColor: '#71758F',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '15px',
                                            fontWeight: 500,
                                            color: '#000000',
                                            userSelect: 'none',
                                            fontFamily: 'inherit',
                                        }}
                                    >
                                        {selectedRow?.isActive ? 'Deactivate' : 'Activate'}
                                    </Typography>
                                </MenuItem>
                                <MenuItem
                                    onClick={handleDeleteClick}
                                    sx={{
                                        padding: '16px 24px !important',
                                        height: 'auto !important',
                                        minHeight: 'unset !important',
                                        boxSizing: 'border-box !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            backgroundColor: '#D4ECFD !important',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, mr: 1.5 }}>
                                        <DeleteMenuIcon width="24px" height="24px" />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '15px',
                                            fontWeight: 500,
                                            color: '#FE2626',
                                            userSelect: 'none',
                                            fontFamily: 'inherit',
                                        }}
                                    >
                                        Delete
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    );
                }
            });
        }
        return columns;
    }

    return (
        <Box sx={{
            mt: 3,
            padding: '20px',
            borderRadius: '8px',
            '--rdg-border-color': '#EFF6FF',
            '& .rdg .rdg-cell.status-chip-cell': {
                padding: '7px 10px !important',
            },
            '& .status-chip-cell': {
                padding: '7px 10px !important',
            },
            '& .rdg .rdg-header-row .rdg-cell, & .rdg-header-row .rdg-cell': {
                borderRight: '1px solid #EFF6FF !important',
                borderInlineEnd: '1px solid #EFF6FF !important',
                '&:last-child': {
                    borderRight: 'none !important',
                    borderInlineEnd: 'none !important',
                },
            },
            '& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(even), & .rdg-row:not(.rdg-header-row):nth-of-type(even), & .rdg .rdg-row.rdg-row-even, & .rdg-row.rdg-row-even': {
                backgroundColor: '#EFF6FF !important',
            },
            '& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(even) .rdg-cell, & .rdg-row:not(.rdg-header-row):nth-of-type(even) .rdg-cell, & .rdg .rdg-row.rdg-row-even .rdg-cell, & .rdg-row.rdg-row-even .rdg-cell': {
                backgroundColor: '#EFF6FF !important',
            },
            '& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(odd), & .rdg-row:not(.rdg-header-row):nth-of-type(odd), & .rdg .rdg-row.rdg-row-odd, & .rdg-row.rdg-row-odd': {
                backgroundColor: '#71758F0D !important',
            },
            '& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(odd) .rdg-cell, & .rdg-row:not(.rdg-header-row):nth-of-type(odd) .rdg-cell, & .rdg .rdg-row.rdg-row-odd .rdg-cell, & .rdg-row.rdg-row-odd .rdg-cell': {
                backgroundColor: '#71758F0D !important',
            },
            '& .rdg .rdg-row:not(.rdg-header-row) .rdg-cell, & .rdg-row:not(.rdg-header-row) .rdg-cell': {
                borderRight: '1px solid #EFF6FF !important',
                borderInlineEnd: '1px solid #EFF6FF !important',
                borderBottom: '1px solid #EFF6FF !important',
                borderBlockEnd: '1px solid #EFF6FF !important',
                borderColor: '#EFF6FF !important',
                '&:last-child': {
                    borderRight: 'none !important',
                    borderInlineEnd: 'none !important',
                },
            },
        }}>
            <DialogueModal width='xl' title='Edit KPI List' isOpen={showCreateModal}
                titleSx={{ padding: '16px 20px' }}
                handleClose={(ev, reason?: string) => {
                    if (reason !== 'backdropClick') {
                        setShowCreateModal(false)
                    }
                }}>
                {!!selectedRow && <KPIForm kpiConfig={selectedRow} onCancel={() => setShowCreateModal(false)}
                    onCreateKPI={() => { setShowCreateModal(false); updateConfigs({ page, pageSize }) }} />}
            </DialogueModal>
            <CustomizedDataGrid
                title={<HighlightedText text="All KPI Configurations" />}
                tableColumns={getColumns()}
                tableData={kpiConfigs || []}
                tableType="KPIList"
                defaultColumnNames={defaultColumns}
                filterDataOptions={filterData}
                listLength={totalCount || 0}
                page={page}
                pageSize={pageSize}
                paginationData={{ totalElements: totalCount, totalPages: Math.ceil(totalCount / pageSize) }}
                onSorting={handleSortingFun}
                currSorting={sortingInfo}
                showExport={true}
                fileName={`KPIConfigs-${moment().format()}.xlsx`}
                exportTableData={() => exportToXlsx(`KPIConfigs-${moment().format()}.xlsx`, kpiConfigs, getColumns())}
                handlepageSizeChange={handlePageSizeChange}
                handlePageChange={handlePageChange}
                isDateRangeFilter={true}
                isDisabled={false}
                rowHeight={40}
            />
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={handleDeleteConfirm}
                identifierName={selectedRow?.ruleName || ""}
                type={'KPI'}
            />
        </Box>
    );
};
